import { useMemo, useState } from 'react';
import type { Service, Staff } from '@salonomy/types';
import { Button, Field, Modal, Pill, Select, TextArea, TextInput } from '@salonomy/ui';
import { useJoinWaitlist } from '../../data/waitlist.js';
import { useAuth } from '../../lib/auth.js';
import { longDate, todayInZone } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS7 phase 4 — "The day is full — join the waitlist". A control to add a client to the waitlist for
 * a date/service and (optionally) a specific stylist. When a cancellation later frees a slot, the API
 * auto-offers it to the best-matching waiting entry over the queue's gap-offer comms (consent-aware,
 * allowance-enforced) — so we make the phone's role explicit: without it the entry still queues but is
 * never auto-notified (we pause, never auto-spend).
 *
 * Sealed-book legible: the entry inherits the chosen stylist's book (a renter's sealed book or the
 * floor); we surface that consequence inline at the moment of joining, mirroring StaffModal's sealed
 * preview, so the operator never discovers the sealing only after the row is created.
 *
 * RBAC mirrors the API (`appointments:write`): a chair_renter may add to their own book only — the
 * staff `Select` is locked to them and "any stylist" is hidden, because an "any" entry would land on
 * the floor, outside their sealed book.
 */

export interface JoinWaitlistProps {
  /** Active bookable staff (the diary's chairs). */
  staff: Staff[];
  /** Active services to choose from. */
  services: Service[];
  tz: string;
  /** Prefill the wanted date (e.g. the full day the diary is showing). Defaults to today. */
  defaultDate?: string;
  /** Prefill the stylist (e.g. the column the user was looking at). */
  defaultStaffId?: string | null;
  onClose: () => void;
}

export function JoinWaitlistModal({
  staff,
  services,
  tz,
  defaultDate,
  defaultStaffId,
  onClose,
}: JoinWaitlistProps) {
  const { user } = useAuth();
  const join = useJoinWaitlist();
  const toast = useToast();

  // A chair_renter is a tenant boundary: they may only add entries to their OWN book. The picker is
  // locked to their staff record and "any stylist" (a floor entry) is not offered. Owner/manager
  // (renterId === null) see the floor and may pick any chair or "any".
  const sealedRenter = Boolean(user?.renterId);
  const lockedStaffId = sealedRenter ? (user?.staffId ?? '') : null;

  const chairs = useMemo(
    () =>
      staff
        .filter((s) => s.active && ['owner', 'stylist', 'chair_renter'].includes(s.role))
        .filter((s) => (lockedStaffId ? s.id === lockedStaffId : true)),
    [staff, lockedStaffId],
  );
  const activeServices = useMemo(() => services.filter((s) => s.active), [services]);

  const [wantedDate, setWantedDate] = useState(defaultDate ?? todayInZone(tz));
  const [serviceId, setServiceId] = useState(activeServices[0]?.id ?? '');
  const [staffId, setStaffId] = useState<string>(
    lockedStaffId ?? defaultStaffId ?? '', // '' = any available stylist
  );
  const [clientName, setClientName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');

  const chosenStaff = chairs.find((s) => s.id === staffId) ?? null;
  // Where will this entry land? A named stylist inherits their book; "any" inherits the actor's own
  // context (a renter's sealed book, or the floor for an owner/manager). Mirror of the API insert.
  const landsSealed = chosenStaff ? Boolean(chosenStaff.renterId) : sealedRenter;
  const sealedOwnerName = chosenStaff?.renterId
    ? chosenStaff.displayName
    : sealedRenter
      ? (user?.displayName ?? 'you')
      : null;

  async function submit() {
    if (!serviceId) {
      toast.error('Pick a service for the waitlist.');
      return;
    }
    if (!clientName.trim()) {
      toast.error('Add the client’s name.');
      return;
    }
    try {
      await join.mutateAsync({
        serviceId,
        wantedDate,
        staffId: staffId || null,
        clientName: clientName.trim(),
        phone: phone.trim() || null,
        note: note.trim() || null,
      });
      toast.success(`Added to the waitlist for ${longDate(wantedDate, tz)}`);
      onClose();
    } catch {
      toast.error('Could not add to the waitlist.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Join the waitlist"
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={join.isPending}>
            Add to waitlist
          </Button>
        </>
      }
    >
      <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
        The day’s full. We’ll hold them in line and offer the first slot that frees up to the best
        match.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Wanted date">
          <TextInput
            type="date"
            value={wantedDate}
            min={todayInZone(tz)}
            onChange={(e) => setWantedDate(e.target.value)}
          />
        </Field>
        <Field label="Service">
          <Select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
            {activeServices.length === 0 ? <option value="">No services</option> : null}
            {activeServices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Field
        label="Stylist"
        hint={
          lockedStaffId
            ? 'You can only waitlist into your own book.'
            : 'Leave as “Any” to take the first stylist who frees up.'
        }
      >
        <Select
          value={staffId}
          disabled={Boolean(lockedStaffId)}
          onChange={(e) => setStaffId(e.target.value)}
        >
          {lockedStaffId ? null : <option value="">Any available stylist</option>}
          {chairs.map((s) => (
            <option key={s.id} value={s.id}>
              {s.displayName}
            </option>
          ))}
        </Select>
      </Field>

      {landsSealed && sealedOwnerName ? (
        <div
          className="muted"
          style={{
            fontSize: 12.5,
            background: 'var(--slm-porcelain)',
            border: '1px solid var(--slm-border)',
            borderRadius: 8,
            padding: '8px 10px',
            margin: '2px 0 6px',
          }}
        >
          This waitlist entry is sealed to {sealedOwnerName}’s book — only they can see it, isolated
          from the rest of the salon.
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Client name">
          <TextInput
            autoFocus
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="e.g. Priya S."
          />
        </Field>
        <Field
          label="Mobile"
          hint={
            phone.trim() ? undefined : 'No number → they queue, but we can’t text them an offer.'
          }
        >
          <TextInput
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+44 7700 900123"
          />
        </Field>
      </div>

      {!phone.trim() ? (
        <div style={{ margin: '-2px 0 6px' }}>
          <Pill tone="neutral" className="mono">
            No reminder number
          </Pill>
        </div>
      ) : null}

      <Field label="Note" hint="Optional — visible to the team">
        <TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. flexible on time, prefers mornings"
        />
      </Field>
    </Modal>
  );
}

/**
 * A ready-to-mount button that opens the join modal — the literal "join the waitlist" control. Drop
 * it on a full diary day or the waitlist panel. Pass the diary's current date/stylist to prefill.
 */
export function JoinWaitlistButton({
  staff,
  services,
  tz,
  defaultDate,
  defaultStaffId,
  size = 'sm',
  variant = 'soft',
  label = 'Join waitlist',
}: Omit<JoinWaitlistProps, 'onClose'> & {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'outline' | 'ghost' | 'danger' | 'soft';
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size={size} variant={variant} onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open ? (
        <JoinWaitlistModal
          staff={staff}
          services={services}
          tz={tz}
          defaultDate={defaultDate}
          defaultStaffId={defaultStaffId}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
